<template>
  <div class="flex flex-col h-full w-full">
    <template v-if="isFirstLoading">
      <div class="flex flex-col items-center content-center justify-around h-full">
        <OrLoaderV3
          :title="$t('home.loading')"
          :description="$t('home.descriptionLoading')"
          variant="linear"
          data-test="queues-loader"
        />
      </div>
    </template>
    <template
      v-else-if="isEmpty"
    >
      <queues-empty />
    </template>
    <template v-else>
      <queues-collection
        :is-loading="isFirstLoading"
        :is-refreshing="isRefreshing"
        :is-searching="isSearching"
        :items="arrayOfQueues"
        data-test="queues-table"
        @open-info="handleOpenInfo"
        @open-edit="handleEditOpen"
        @open-modal="handleOpenMonitoring"
        @delete="handleDeleteQueue"
        @refresh="handleRefresh"
        @scroll="handleScroll"
        @search="handleSearch"
      />
    </template>
    <queues-add-button
      class="fixed right-0 bottom-0 mx-xl my-lg+"
      data-test="queues-add-button"
      @click="toggleAddOpen(true)"
    />
    <queues-add-modal
      :is-open="isAddModalOpened"
      :is-submitting="isAdding"
      @add="handleAdd"
      @close="toggleAddOpen(false)"
    />
    <queue-edit-modal
      :is-open="isEditModalOpened"
      :is-updating="isUpdating"
      :queue="currentQueue"
      @update="handleUpdate"
      @delete="handleDeleteQueue"
      @close="toggleEditModalOpen(false)"
    />
    <queue-delete-modal
      v-if="!!queueToDelete"
      :queue-name="queueToDelete.name"
      :is-open="isDeleteOpen"
      :is-loading="isDeleting"
      @accept="handleDelete"
      @decline="handleDeleteCancel"
    />
    <queue-info-modal
      :queue-attributes="queueAttributes"
      :is-open="isInfoModalOpen"
      :is-loading="isGettingAttributes"
      @close="toggleInfoOpen(false)"
    />
    <queue-monitoring-modal
      :metric-data="monitoringData"
      :is-open="isMonitoringModalOpen"
      :is-loading="isGettingMetricData"
      @update-params="updateMonitoring"
      @close="toggleMonitoringOpen(false)"
    />
  </div>
</template>

<script lang="ts">

import { useToggle } from '@vueuse/core';
import { defineComponent } from 'vue';
import {
  CreateQueue,
  Queue,
  UpdateQueue,
} from '@/types';
import { CHART_NAME } from '@/constants';
import { OrLoaderV3 } from '@onereach/ui-components';
import { QueuesCollection, QueuesEmpty } from '../components/QueuesCollection';
import { QueuesAddButton, QueuesAddModal, QueueEditModal } from '../components/QueuesModal';
import { QueueDeleteModal } from '../components/QueuesDelete';
import { QueueInfoModal } from '../components/QueueInfo';
import { QueueMonitoringModal } from '../components/QueueMonitoring';
import { storeToRefs } from 'pinia';
import { useQueuesStore } from '../stores';
import _ from 'lodash';

export default defineComponent({
  name: 'QueuesView',
  components: {
    QueuesCollection,
    QueuesAddButton,
    QueuesAddModal,
    QueueEditModal,
    QueueDeleteModal,
    QueueInfoModal,
    QueueMonitoringModal,
    QueuesEmpty,
    OrLoaderV3,
  },
  setup() {
    const queuesStore = useQueuesStore();
    const {
      isLoading,
      queues,
      next,
      queueAttributes,
      monitoringData,
      queueByName,
    } = storeToRefs(queuesStore);
    const {
      getList,
      createQueue,
      updateQueue,
      getAttributes,
      deleteQueue,
      getMetricData,
      scrollQueues,
      getQueueByName,
    } = queuesStore;

    const [isAddModalOpened, toggleAddOpen] = useToggle();
    const [isAdding, toggleAdding] = useToggle();
    const [isEditModalOpened, toggleEditModalOpen] = useToggle();
    const [isUpdating, toggleUpdating] = useToggle();
    const [isInfoModalOpen, toggleInfoOpen] = useToggle();
    const [isGettingAttributes, toggleGettingAttributes] = useToggle();
    const [isMonitoringModalOpen, toggleMonitoringOpen] = useToggle();
    const [isGettingMetricData, toggleGettingMetricData] = useToggle();
    const [isDeleteOpen, toggleDeleteOpen] = useToggle();
    const [isDeleting, toggleDeleting] = useToggle();
    const [isScrolling, toggleScrolling] = useToggle();
    const [isSearching, toggleSearching] = useToggle();
    const [isFirstLoading, toggleFirstLoading] = useToggle();
    const [isRefreshing, toggleRefreshing] = useToggle();

    return {
      isLoading,
      queues,
      next,
      queueAttributes,
      monitoringData,
      queueByName,
      getList,
      createQueue,
      updateQueue,
      getAttributes,
      deleteQueue,
      getMetricData,
      scrollQueues,
      getQueueByName,
      isAddModalOpened,
      toggleAddOpen,
      isAdding,
      toggleAdding,
      isEditModalOpened,
      toggleEditModalOpen,
      isUpdating,
      toggleUpdating,
      isDeleting,
      toggleDeleting,
      isDeleteOpen,
      toggleDeleteOpen,
      isInfoModalOpen,
      toggleInfoOpen,
      isGettingAttributes,
      toggleGettingAttributes,
      isMonitoringModalOpen,
      toggleMonitoringOpen,
      isGettingMetricData,
      toggleGettingMetricData,
      isScrolling,
      toggleScrolling,
      isSearching,
      toggleSearching,
      isFirstLoading,
      toggleFirstLoading,
      isRefreshing,
      toggleRefreshing,
    };
  },
  data() {
    return {
      queueToDelete: null as Queue | null,
      currentQueue: {} as Queue,
      period: 'Last 1 hour',
      scale: '5 min',
      arrayOfQueues: [] as Queue[],
      arrayOfSearchedQueues: [] as Queue[],
      createdQueues: [] as Queue[],
      currentNext: '',
      searchStr: '',
    };
  },
  computed: {
    isEmpty(): boolean {
      return this.queues?.length === 0;
    },
  },
  watch: {
    searchStr: {
      handler: function () {
        this.searchScroll();
      },
    },
    currentNext: {
      handler: function () {
        if (this.currentNext !== '' && this.isSearching) this.repeatScrollWithSearch();
      },
    },
  },

  async beforeMount(): Promise<void> {
    this.toggleFirstLoading(true);
    await this.loadFirsQueues();
    this.toggleFirstLoading(false);
  },

  methods: {
    async loadFirsQueues(): Promise<void> {
      this.currentNext = '';
      this.createdQueues = [];
      this.arrayOfQueues = [];
      await this.scrollItems();
    },

    async searchScroll(): Promise<void> {
      if (this.searchStr !== '') {
        this.toggleSearching(true);
        this.currentNext = '';
        this.arrayOfSearchedQueues = [];
        await this.scrollItemsWithSearch();
      } else {
        this.toggleSearching(false);
        await this.loadFirsQueues();
      }
    },

    async repeatScrollWithSearch(): Promise<void> {
      if (this.currentNext !== 'end') await this.scrollItemsWithSearch();
      else {
        this.toggleSearching(false);
        this.arrayOfQueues = [...this.arrayOfSearchedQueues];
      }
    },

    async scrollItems(): Promise<void> {
      if (this.currentNext !== 'end') {
        this.toggleScrolling(true);
        await this.scrollQueues({
          amount: 10,
          next: this.currentNext,
        });
        const array = _.reject([...this.arrayOfQueues, ...this.queues], queue => _.find(this.createdQueues, queue) !== undefined);
        this.arrayOfQueues = [...this.createdQueues, ...array];
        this.toggleScrolling(false);
      }
    },

    async scrollItemsWithSearch(): Promise<void> {
      if (this.currentNext !== 'end') {
        await this.scrollQueues({
          amount: 10,
          next: this.currentNext,
        });
        const filterArray = _.filter([...this.arrayOfSearchedQueues, ...this.queues], queue => queue.name.indexOf(this.searchStr) !== -1);
        this.arrayOfSearchedQueues = [...filterArray];
        this.currentNext = this.next;
      }
    },

    async handleScroll(): Promise<void> {
      this.currentNext = this.next;
      await this.scrollItems();
    },

    async getMonitoring(): Promise<void> {
      return await this.getMetricData({
        chartName: JSON.stringify(CHART_NAME),
        queueName: this.currentQueue.name,
        period: this.period,
        scale: this.scale,
      });
    },

    async handleAdd(data: CreateQueue): Promise<void> {
      this.toggleAddOpen(true);
      this.toggleAdding(true);
      try {
        await this.createQueue(data);
        await this.getQueueByName(data.queueName);
        if (this.queueByName) {
          this.createdQueues.unshift(this.queueByName);
        }
        this.currentNext = '';
        this.arrayOfQueues = [];
        await this.scrollItems();
      }
      finally {
        this.toggleAdding(false);
        this.toggleAddOpen(false);
      }
    },

    async handleUpdate(data: UpdateQueue): Promise<void> {
      this.toggleUpdating(true);
      try {
        await this.updateQueue(data);
        await this.searchScroll();
      }
      finally {
        this.toggleUpdating(false);
        this.toggleEditModalOpen(false);
      }
    },

    async handleEditOpen(queue: Queue): Promise<void> {
      this.toggleEditModalOpen(true);
      this.currentQueue = queue;
    },

    async handleOpenInfo(queue: Queue): Promise<void> {
      this.toggleInfoOpen(true);
      this.toggleGettingAttributes(true);
      try {
        await this.getAttributes({ queueUrl: queue.url });
      }
      finally {
        this.currentQueue = queue;
        this.toggleGettingAttributes(false);
      }
    },

    async handleOpenMonitoring(queue: Queue): Promise<void> {
      this.toggleMonitoringOpen(true);
      this.toggleGettingMetricData(true);
      this.currentQueue = queue;
      try {
        await this.getMonitoring();
      }
      finally {
        this.toggleGettingMetricData(false);
      }
    },

    async updateMonitoring(value: any): Promise<void> {
      this.toggleGettingMetricData(true);
      this.period = value.period;
      this.scale = value.scale;
      try {
        await this.getMonitoring();
      }
      finally {
        this.toggleGettingMetricData(false);
      }
    },

    handleDeleteQueue(queue: Queue): void {
      this.queueToDelete = queue;
      this.toggleDeleteOpen(true);
    },

    async handleDelete(): Promise<void> {
      this.toggleDeleting(true);
      try {
        if (this.queueToDelete) await this.deleteQueue({ queueId: this.queueToDelete.queueId });
        this.queueToDelete = null;
        await this.searchScroll();
        this.toggleDeleteOpen(false);
      }
      finally {
        this.toggleDeleting(false);
      }
    },

    handleDeleteCancel(): void {
      this.queueToDelete = null;
      this.toggleDeleteOpen(false);
    },

    handleSearch(searchStr: string): void {
      this.searchStr = searchStr;
    },

    async handleRefresh(): Promise<void> {
      this.toggleRefreshing(true);
      await this.searchScroll();
      this.toggleRefreshing(false);
    },

  },
});
</script>
